import { useKeycloak } from 'all-in-one-react-keycloak';
import { CLAIM_STATUSES, InboxMessage, MessageCategory, usePatchInboxMessage } from 'all-in-one-shared-api';
import { Badge, Collapse, Empty, Space, Typography } from 'antd';
import InboxIcon from 'assets/images/inbox/inbox.svg';
import { useInboxMessagesContext } from 'contexts/inboxMessages';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { formatDateString, isSameOrBefore } from 'utils';
import { isLocaleEnglish } from 'utils/languageUtils';
import { StyledTitle } from 'components/StyledTitle';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import EnvelopeTicked from 'assets/images/envelope-ticked.svg';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const HtmlToReactParser = require('html-to-react').Parser;
const { Text: AntdText, Paragraph: AntdParagraph } = Typography;
const { Panel } = Collapse;

const Text = styled(AntdText)`
  font-size: 1.14rem;
`;

const DangerText = styled(AntdText)`
  color: var(--error-color);
`;

const Paragraph = styled(AntdParagraph)`
  color: var(--grey-800);
  font-size: 1.14rem;
`;

const TitleSection = styled(Space)`
  margin-bottom: 4.285rem;
`;

const GroupTitle = styled.div`
  margin: 1rem 0;
`;
const EmptyInboxDescription = styled.div`
  font-size: 24px;
  font-weight: 500;
`;

function InboxPage() {
  const { t, i18n } = useTranslation();
  const { inboxMessages, fetchInboxMessages } = useInboxMessagesContext();
  const [messageId, setMessageId] = useState('');
  const { keycloak } = useKeycloak();
  const navigate = useNavigate();
  const { shouldFetch: shouldPatchInboxMessage } = usePatchInboxMessage(
    `markAsRead/${messageId}`,
    keycloak.token ?? '',
    messageId,
    {
      onSuccess: () => {
        shouldPatchInboxMessage(false);
        fetchInboxMessages();
      },
      onError: () => {
        shouldPatchInboxMessage(false);
      },
    },
  );

  const onChange = (key: string | string[]) => {
    if (!key || typeof key !== 'string') {
      return;
    }
    const selectedMessage = inboxMessages.find((message) => message.id.toString() === key);
    const isRead = !!selectedMessage?.shownDate;
    if (!isRead) {
      setMessageId(key);
      shouldPatchInboxMessage(true);
    }
  };

  useEffect(() => {
    fetchInboxMessages();
  }, [fetchInboxMessages]);

  const groupInBoxMessageByCreateDate = useMemo(() => {
    const last7Days: InboxMessage[] = [];
    const last30Days: InboxMessage[] = [];
    const last6Months: InboxMessage[] = [];
    const olderThan6Months: InboxMessage[] = [];
    inboxMessages.forEach((message) => {
      if (isSameOrBefore(moment(message.createDate), 7)) {
        last7Days.push(message);
      } else if (isSameOrBefore(moment(message.createDate), 30)) {
        last30Days.push(message);
      } else if (isSameOrBefore(moment(message.createDate), 30 * 6)) {
        last6Months.push(message);
      } else {
        olderThan6Months.push(message);
      }
    });
    const inboxMessagesGrouped: Record<string, InboxMessage[]> = {};
    if (last7Days.length > 0) {
      inboxMessagesGrouped[t('inbox.last7Days')] = last7Days;
    }
    if (last30Days.length > 0) {
      inboxMessagesGrouped[t('inbox.last30Days')] = last30Days;
    }
    if (last6Months.length > 0) {
      inboxMessagesGrouped[t('inbox.last6Months')] = last6Months;
    }
    if (olderThan6Months.length > 0) {
      inboxMessagesGrouped[t('inbox.olderThan6Months')] = olderThan6Months;
    }
    return inboxMessagesGrouped;
  }, [inboxMessages, t]);

  const getMessageTitle = useCallback(
    ({ messageCategory, messageTitleEn, messageTitleZh, policyNumber }: InboxMessage) => {
      const messageTitle = (isLocaleEnglish(i18n.language) ? messageTitleEn : messageTitleZh) ?? '';
      let title = messageTitle.length ? messageTitle : t(`inbox.messageCategory.${messageCategory}`);
      if (policyNumber) {
        title = title.replaceAll('{claimNo}', `#${policyNumber}`);
      }
      return <Text strong>{title}</Text>;
    },
    [i18n.language, t],
  );

  const parsedMessage = ({ message, message_zh }: { message: string; message_zh: string }) => {
    const msg = isLocaleEnglish(i18n.language) ? message : message_zh;
    const htmlToReactParser = new HtmlToReactParser();
    return htmlToReactParser.parse(msg);
  };

  const buildLink = (id: string, category: MessageCategory) => {
    switch (category) {
      case MessageCategory.CLAIM_COMPLETED:
        return `/claims/${id}?sequenceNo=1&claimStatus=${CLAIM_STATUSES.COMPLETED}`;
      case MessageCategory.CLAIM_NEEDACTION:
        return `/claims/${id}?sequenceNo=1&claimStatus=${CLAIM_STATUSES.NEED_ACTION}`;
      case MessageCategory.CLAIM_DECLINED:
        return `/claims/${id}?sequenceNo=1&claimStatus=${CLAIM_STATUSES.DECLINED}`;
      case MessageCategory.SHORTFALL_SETTLED:
        return `/claims/${id}?sequenceNo=1&claimStatus=${CLAIM_STATUSES.SHORTFALL}`;
      default:
        return '';
    }
  };

  const canGoToDetailPage = (messageCategory: MessageCategory) => {
    const claimsAllowNavigate = [
      MessageCategory.CLAIM_COMPLETED,
      MessageCategory.CLAIM_NEEDACTION,
      MessageCategory.CLAIM_DECLINED,
      MessageCategory.SHORTFALL_SETTLED,
    ];
    return claimsAllowNavigate.includes(messageCategory);
  };

  const gotoDetailPage = (message: string, messageCategory: MessageCategory) => {
    if (!canGoToDetailPage(messageCategory)) return;
    const regexNumberOnly = /(\d+)/;
    const matchedRegex = message.match(regexNumberOnly);
    if (!matchedRegex?.length) return;
    const link = buildLink(matchedRegex[0], messageCategory);
    if (!link) return;
    navigate(link);
  };

  return (
    <>
      <TitleSection align="center">
        <img src={InboxIcon} />
        <StyledTitle level={2}>{t('inbox.bannerHeading')}</StyledTitle>
      </TitleSection>

      {Object.keys(groupInBoxMessageByCreateDate).length > 0 ? (
        Object.keys(groupInBoxMessageByCreateDate).map((title) => {
          return (
            <div key={title}>
              <GroupTitle>
                <Text data-testid={`mock-GroupTitle-${title}`} type="secondary">
                  {title}
                </Text>
              </GroupTitle>
              <Collapse accordion onChange={onChange}>
                {groupInBoxMessageByCreateDate[title].map((msg) => (
                  <Panel
                    key={msg.id}
                    header={
                      <div style={{ width: '100%' }}>
                        <Space size="middle">
                          <Text type="secondary">{formatDateString(msg.effectiveDate)}</Text>
                          {getMessageTitle(msg)}
                          {!msg.shownDate && (
                            <Badge color="var(--error-color)" text={<DangerText>{t('inbox.new')}</DangerText>} />
                          )}
                        </Space>
                      </div>
                    }
                  >
                    <Paragraph
                      style={{ cursor: canGoToDetailPage(msg.messageCategory) ? 'pointer' : 'auto' }}
                      onClick={() => gotoDetailPage(msg.message, msg.messageCategory)}
                    >
                      {parsedMessage({ message: msg.message, message_zh: msg.message_zh })}
                    </Paragraph>
                  </Panel>
                ))}
              </Collapse>
            </div>
          );
        })
      ) : (
        <Empty
          image={EnvelopeTicked}
          description={<EmptyInboxDescription> {t('inbox.emptyInbox')}</EmptyInboxDescription>}
        />
      )}
    </>
  );
}

export default InboxPage;
